import { createContext, useContext, useEffect } from 'react';

export interface PageTitleContextValue {
  title: string;
  helpText?: string;
  setTitle: (title: string, helpText?: string) => void;
}

export const PageTitleContext = createContext<PageTitleContextValue | undefined>(
  undefined,
);

/**
 * Read the current page title — used by the global `AppTopBar` to render
 * the active page's title in the navbar. Returns an empty string when no
 * page has declared a title (e.g. the chat / editor screens), which the
 * `TopBar` renders as an empty left slot.
 */
export const usePageTitleValue = (): string => {
  const ctx = useContext(PageTitleContext);
  return ctx?.title ?? '';
};

export const usePageHelpTextValue = (): string | undefined => {
  const ctx = useContext(PageTitleContext);
  return ctx?.helpText;
};

/**
 * Declare the title for the current page. The title shows in the global
 * navbar (`AppTopBar`) instead of inside the page body. Clears on unmount
 * so navigating to a title-less screen doesn't leave a stale title behind.
 */
export const usePageTitle = (title: string, helpText?: string): void => {
  const ctx = useContext(PageTitleContext);
  const setTitle = ctx?.setTitle;
  useEffect(() => {
    if (!setTitle) return;
    setTitle(title, helpText);
    return () => setTitle('');
  }, [title, helpText, setTitle]);
};
